import React, { type FC } from 'react';
import { DataTable, type DataTableStateEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ConfirmDialog } from 'primereact/confirmdialog';
import {
  type PeriodGroupItem,
  type PeriodItem,
  PeriodType,
  TimePeriodOptions,
} from '../../models/Period';
import type { IDriver } from '../../models/Driver';
import {
  capitalizeFirstLetter,
  formatMileage,
  getPeriodEnum,
  noop,
} from '../../utils/GeneralUtils';
import { ReactComponent as CopyIcon } from '../../assets/ic_copy.svg';
import { ReactComponent as TripsIcon } from '../../assets/ic_trips.svg';
import styles from './Drivers.module.scss';
import DateUtils from '../../utils/DateTimeUtils';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import UserSearch from '../../components/UserSearch';
import AppSetupCheck from '../../components/AppSetupCheck';
import useTenantStore, { selectTenantId } from '../../stores/TenantStore';
import OpenAPI from '../../endpoint/Openapi';
import { type paths } from '../../endpoint/Schema';
import { useTestUsers } from '../../contexts/TestUsersContext';
import { useNotInWhiteListUsers } from '../../contexts/NotInWhiteListUsersContext';
import { useNotCustomerUsers } from '../../contexts/NotCustomerUsersContext';
import { useDemoMode } from '../../contexts/DemoModeContext';
import { maskFullName, maskEmail } from '../../utils/piiMasking';

const ROWS = 10;

type TableParams = Pick<
  DataTableStateEvent,
  'first' | 'rows' | 'sortField' | 'sortOrder'
>;
// interface ITrackingStatusOption {
//   name: TrackingStatus;
// }

// const TrackingStatusOptions: ITrackingStatusOption[] = [
//   {
//     name: 'On',
//   },
//   {
//     name: 'Off',
//   },
//   {
//     name: 'NoData',
//   },
// ];

const Drivers: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = useTenantStore(selectTenantId);
  const { isDemoModeActive } = useDemoMode();
  const { includeTestUsers } = useTestUsers();
  const { includeNotInWhiteListUsers } = useNotInWhiteListUsers();
  const { includeNotCustomerUsers } = useNotCustomerUsers();
  const [selectedPeriod, setSelectedPeriod] = React.useState<PeriodType>(
    PeriodType.LAST_7_DAY,
  );
  const [registrationDateFilter, setRegistrationDateFilter] = React.useState<
    string | Date | undefined
  >();
  const [tripsCountFilter, setTripsCountFilter] = React.useState<
    string | undefined
  >();
  // const [trackingStatusFilter, setTrackingStatusFilter] = React.useState<
  //   TrackingStatus | undefined
  // >();
  const [lazyParams, setLazyParams] = React.useState<
    Pick<
      DataTableStateEvent,
      'first' | 'rows' | 'page' | 'sortField' | 'sortOrder' | 'search'
    >
  >({
    first: +(searchParams.get('first') ?? '0'),
    rows: ROWS,
    page: 1,
    search: '',
    sortField: 'registrationDate',
    sortOrder: 1,
  });
  const [loading, setLoading] = React.useState(true);
  const [data, setData] =
    React.useState<
      paths['/api/v2/admin/drivers']['get']['responses']['200']['content']['application/json']
    >();
  const toast = React.useRef<Toast>(null);

  React.useEffect(() => {
    const getDrivers = async () => {
      setLoading(true);

      try {
        const res = await OpenAPI.GET('/api/v2/admin/drivers', {
          params: {
            query: {
              tenantId,
              period: getPeriodEnum(selectedPeriod),
              columnOrderName:
                lazyParams.sortField != null
                  ? capitalizeFirstLetter(lazyParams.sortField)
                  : 'RegistrationDate',
              orderType: lazyParams.sortOrder === 1 ? 'Desc' : 'Asc',
              registrationDate:
                registrationDateFilter != null
                  ? DateUtils.formatToUTC(registrationDateFilter)
                  : undefined,
              // trackingStatus: trackingStatusFilter,
              includeTestUsers,
              includeNotInWhiteListUsers,
              includeNotCustomerUsers,
              skip: lazyParams.first,
              take: lazyParams.rows,
            },
          },
        });

        if (res.data) setData(res.data);
      } catch {}

      setLoading(false);
    };

    getDrivers().catch(noop);
  }, [
    lazyParams.first,
    lazyParams.rows,
    lazyParams.sortField,
    lazyParams.sortOrder,
    registrationDateFilter,
    selectedPeriod,
    tenantId,
    includeTestUsers,
    includeNotInWhiteListUsers,
    includeNotCustomerUsers,
  ]);

  function onSelectedPeriodChange(event: { value: PeriodType }): void {
    setSelectedPeriod(event.value);
  }

  const groupedItemTemplate = (option: PeriodGroupItem): JSX.Element => {
    return (
      <div className="flex align-items-center text-xl">
        <div>{option.label}</div>
      </div>
    );
  };

  const periodItemTemplate = (option: PeriodItem): JSX.Element => {
    return (
      <div className="pl-6 text-xl">
        <div>{option.label}</div>
      </div>
    );
  };

  function handlePageChange(event: DataTableStateEvent) {
    setLazyParams((prev) => ({ ...prev, ...event }));

    const params = event as TableParams;
    const o = {
      first: params.first.toString(),
      rows: params.rows.toString(),
      sortField: params.sortField.toString(),
      sortOrder: params.sortOrder?.toString() ?? '1',
    };
    setSearchParams(o);
  }

  function userIdBodyTemplate(rowData: IDriver) {
    function copyId() {
      navigator.clipboard
        .writeText(rowData.id)
        .then(() => {
          toast.current?.show({
            severity: 'success',
            summary: `ID copied`,
            life: 3000,
          });
        })
        .catch((err) => {
          toast.current?.show({
            severity: 'error',
            summary: 'Error occurred',
            detail: err instanceof Error ? err.message : '',
            life: 3000,
          });
        });
    }

    return (
      <button onClick={copyId}>
        <CopyIcon className={styles.copyIcon} />
      </button>
    );
  }

  function nameBodyTemplate(rowData: IDriver) {
    if (isDemoModeActive) {
      return maskFullName(rowData.givenName, rowData.surname);
    }
    return [rowData.givenName, rowData.surname].filter(Boolean).join(' ');
  }

  function emailBodyTemplate(rowData: IDriver) {
    if (isDemoModeActive) {
      return maskEmail(rowData.email);
    }
    return rowData.email;
  }

  function registrationDateBodyTemplate(rowData: IDriver) {
    return DateUtils.formatDate(rowData.registrationDate);
  }

  function registrationDateFilterTemplate() {
    return (
      <div className="flex flex-row items-center">
        <Calendar
          value={registrationDateFilter}
          onChange={(e) => {
            if (e.value != null && !Array.isArray(e.value))
              setRegistrationDateFilter(e.value);
          }}
          dateFormat="mm/dd/yy"
          placeholder="mm/dd/yyyy"
          mask="99/99/9999"
        />
        <button
          onClick={() => {
            setRegistrationDateFilter(undefined);
          }}
          className="ml-2 font-thin text-[1.3rem]"
        >
          X
        </button>
      </div>
    );
  }

  function mileageDateBodyTemplate(rowData: IDriver) {
    return formatMileage(rowData.mileage);
  }

  function tripsBodyTemplate(rowData: IDriver) {
    return (
      <Link to={'/trips?userId=' + rowData.id + '&period=' + selectedPeriod}>
        <TripsIcon className={styles.copyIcon} />
      </Link>
    );
  }

  function drivingScoreBodyTemplate(rowData: IDriver) {
    return rowData.drivingScore ? Math.round(rowData.drivingScore) : '-';
  }

  function accelerationScoreBodyTemplate(rowData: IDriver) {
    return rowData.accelerationScore
      ? Math.round(rowData.accelerationScore)
      : '-';
  }

  function brakingScoreBodyTemplate(rowData: IDriver) {
    return rowData.brakingScore ? Math.round(rowData.brakingScore) : '-';
  }

  function corneringScoreBodyTemplate(rowData: IDriver) {
    return rowData.corneringScore ? Math.round(rowData.corneringScore) : '-';
  }

  function speedingScoreBodyTemplate(rowData: IDriver) {
    return rowData.speedingScore ? Math.round(rowData.speedingScore) : '-';
  }

  function phoneUsageScoreBodyTemplate(rowData: IDriver) {
    return rowData.phoneUsageScore ? Math.round(rowData.phoneUsageScore) : '-';
  }

  // function trackingStatusBodyTemplate(rowData: IDriver) {
  //   return (
  //     <span
  //       className={classNames(
  //         styles.trackingStatus,
  //         rowData.trackingStatus === 'On' && styles.statusOn,
  //         rowData.trackingStatus === 'Off' && styles.statusOff,
  //         rowData.trackingStatus === 'NoData' && styles.statusNoData,
  //       )}
  //     >
  //       {rowData.trackingStatus}
  //     </span>
  //   );
  // }

  function tripsCountFilterTemplate() {
    return (
      <div className="flex flex-row items-center">
        <InputText
          value={tripsCountFilter}
          onChange={(e) => {
            setTripsCountFilter(e.target.value);
          }}
          placeholder=""
        />
        <button
          onClick={() => {
            setTripsCountFilter('');
          }}
          className="ml-2 font-thin text-[1.3rem]"
        >
          X
        </button>
      </div>
    );
  }

  function deletionStatusBodyTemplate(rowData: IDriver) {
    if (rowData.isDeleteRequested) {
      return (
        <div className="text-orange-600">
          Deletion Requested
          {rowData.deleteRequestedAt && (
            <div className="text-xs text-gray-500">
              {DateUtils.formatDateUK(rowData.deleteRequestedAt)}
            </div>
          )}
        </div>
      );
    }
    return <div className="text-green-600">Active</div>;
  }

  function userTypeBodyTemplate(rowData: IDriver) {
    const getUserTypeColor = (userType: string | undefined) => {
      switch (userType) {
        case 'User':
          return 'text-green-600 bg-green-100';
        case 'Tester':
          return 'text-blue-600 bg-blue-100';
        case 'NotInWhiteList':
          return 'text-red-600 bg-red-100';
        default:
          return 'text-gray-600 bg-gray-100';
      }
    };

    return (
      <div className="flex justify-center">
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${getUserTypeColor(rowData.userType)}`}
        >
          {rowData.userType ?? 'Unknown'}
        </span>
      </div>
    );
  }

  // function trackingStatusFilterTemplate() {
  //   return (
  //     <div className="flex flex-row items-center">
  //       <Dropdown
  //         value={trackingStatusFilter}
  //         options={TrackingStatusOptions}
  //         onChange={(e) => {
  //           setTrackingStatusFilter(e.value);
  //         }}
  //         optionLabel="name"
  //         placeholder="Any"
  //         className="p-column-filter"
  //       />
  //       <button
  //         onClick={() => {
  //           setTrackingStatusFilter(undefined);
  //         }}
  //         className="ml-2 font-thin text-[1.3rem]"
  //       >
  //         X
  //       </button>
  //     </div>
  //   );
  // }

  return (
    <div className="w-full">
      <div>
        <div>Statistics time period</div>
        <Dropdown
          options={TimePeriodOptions}
          value={selectedPeriod}
          onChange={onSelectedPeriodChange}
          optionLabel="label"
          optionGroupLabel="label"
          optionGroupChildren="items"
          placeholder="Last 7 days"
          className="p-2 min-w-[300px]"
          optionGroupTemplate={groupedItemTemplate}
          itemTemplate={periodItemTemplate}
        />
      </div>

      <br />
      <UserSearch
        tenantId={tenantId}
        handleNewUserSelected={(e) => {
          navigate('/user/' + e.id);
        }}
        handleInputOnChange={(e) => e}
      />
      <br />
      <br />

      <DataTable
        value={data?.page ?? []}
        dataKey="id"
        scrollable
        paginator
        rows={ROWS}
        first={lazyParams.first}
        totalRecords={data?.count ?? 0}
        loading={loading}
        rowHover
        lazy
        onPage={handlePageChange}
        onSort={handlePageChange}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        filterDisplay="row"
        onRowClick={(e) => {
          navigate('/user/' + e.data.id);
        }}
      >
        <Column field="id" header="User ID" body={userIdBodyTemplate} />
        <Column field="name" header="Name" body={nameBodyTemplate} />
        <Column
          field="registrationDate"
          header="Registration Date"
          sortable
          body={registrationDateBodyTemplate}
          // filter
          filterElement={registrationDateFilterTemplate}
          filterMatchMode="equals"
          showFilterMatchModes={false}
        />
        <Column field="email" header="Email" body={emailBodyTemplate} />
        <Column
          field="drivingScore"
          header="Safety Score"
          sortable
          body={drivingScoreBodyTemplate}
        />
        <Column
          field="accelerationScore"
          header="Acceleration"
          body={accelerationScoreBodyTemplate}
        />
        <Column
          field="brakingScore"
          header="Braking"
          body={brakingScoreBodyTemplate}
        />
        <Column
          field="corneringScore"
          header="Cornering"
          body={corneringScoreBodyTemplate}
        />
        <Column
          field="speedingScore"
          header="Speeding"
          body={speedingScoreBodyTemplate}
        />
        <Column
          field="phoneUsageScore"
          header="Phone Usage"
          body={phoneUsageScoreBodyTemplate}
        />
        <Column
          field="mileage"
          header="Mileage"
          sortable
          body={mileageDateBodyTemplate}
        />
        <Column field="trips" header="Trips" body={tripsBodyTemplate} />
        <Column
          field="tripCounts"
          header="Total Trips Count"
          bodyClassName="text-center"
          style={{ textAlign: 'center' }}
          sortable
          // filter
          filterElement={tripsCountFilterTemplate}
          showFilterMatchModes={false}
        />
        <Column
          field="deletionStatus"
          header="Status"
          body={deletionStatusBodyTemplate}
        />
        <Column
          field="userType"
          header="User Type"
          body={userTypeBodyTemplate}
          bodyClassName="text-center"
          style={{ minWidth: '200px' }}
        />
      </DataTable>

      <AppSetupCheck pageTitle="Drivers" />
      <Toast ref={toast} className={styles.toast} />
      <ConfirmDialog />
    </div>
  );
};

export default Drivers;
